import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  MultiSelect,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import {
  LuBell,
  LuChevronLeft,
  LuFileText,
  LuLayoutGrid,
  LuListChecks,
  LuPlus,
  LuSettings,
  LuSparkles,
  LuAlertTriangle,
} from "react-icons/lu";

import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import CustomPagination from "../../../components/Pagination/CustomPagination";
import { NoteCard } from "./NoteCard";
import NoteTypeStudio from "./studio/NoteTypeStudio";
import {
  getAllNoteTypes,
  getFieldTypes,
  getNoteTypes,
} from "../../../services/note";
import { getAllStaff } from "../../../services/staff";
import {
  getClientOtherProviders,
  getNoteSettings,
  updateNoteSettings,
} from "../../../services/noteSettings";
import { TrackPageView } from "../../../config/mixpanelConfig";
import useSettingsStyles from "./settingsStyle";

// Defaults mirror the backend model so the UI renders sensibly before the
// settings document loads (and if a field is ever missing).
const DEFAULT_SETTINGS = {
  general: { autoSaveDrafts: true, lockAfterApproval: true },
  notifications: { emailApproversOnSubmit: true, notifyStaffOnRejection: true },
  incident: { adminRecipients: [], notifyGuardiansPOA: true, otherProviders: [] },
  ai: { aiNoteGeneration: true, voiceToText: true },
  reports: { includeLetterhead: true, approvedNotesOnly: true },
};

const NAV = [
  { key: "general", label: "General", group: "Configuration", icon: <LuSettings size={16} /> },
  { key: "templates", label: "Templates", group: "Configuration", icon: <LuLayoutGrid size={16} /> },
  { key: "notifications", label: "Notifications", group: "Configuration", icon: <LuBell size={16} /> },
  { key: "incident", label: "Incident Notes", group: "Configuration", icon: <LuAlertTriangle size={16} /> },
  // { key: "ai", label: "AI & Voice", group: "Account", icon: <LuSparkles size={16} /> },
  { key: "reports", label: "Reports & PDF", group: "Account", icon: <LuFileText size={16} /> },
];

const initials = (name = "") =>
  name
    .replace(/[^A-Za-z ]/g, "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Defined at module scope (not inside the page component) so their identity is
// stable across renders — otherwise text inputs inside them would remount and
// lose focus on every keystroke.
const SettingRow = ({ classes, label, desc, checked, onChange, disabled }) => (
  <Box className={classes.srow}>
    <Box>
      <Text className={classes.srowLab}>{label}</Text>
      {desc && <Text className={classes.srowDsc}>{desc}</Text>}
    </Box>
    <Switch
      color="yellow"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  </Box>
);

const SettingCard = ({ classes, title, sub, action, children }) => (
  <Box className={classes.card}>
    <Box className={classes.cardHead}>
      <Box>
        <Text className={classes.cardHeadTitle}>{title}</Text>
        {sub && <Text className={classes.cardHeadSub}>{sub}</Text>}
      </Box>
      {action}
    </Box>
    <Box className={classes.cardBody}>{children}</Box>
  </Box>
);

const NewNoteType = () => {
  const { classes, cx } = useSettingsStyles();
  const navigate = useNavigate();

  const { profile } = useSelector((state) => state?.profile);
  const { noteTypes, loading } = useSelector((state) => state.note);
  const { allStaff } = useSelector((state) => state.staff);

  const [section, setSection] = useState(
    typeof window !== "undefined" && window.location.hash === "#templates"
      ? "templates"
      : "templates"
  );
  const [currentPage, setCurrentPage] = useState(1);
  // Templates haven't been fetched yet on first mount — `loading` is still
  // false at that point, so track the initial fetch separately to show the loader.
  const [initialLoading, setInitialLoading] = useState(true);
  // Note Type Studio overlay — { open, note } where note=null means "new template".
  const [studio, setStudio] = useState({ open: false, note: null });
  const openStudio = (note = null) => setStudio({ open: true, note });
  const closeStudio = () => setStudio({ open: false, note: null });
  const refreshTypes = () => {
    getNoteTypes(currentPage);
    getAllNoteTypes();
  };

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // Read-only providers aggregated from every client's Care & Support record.
  const [clientProviders, setClientProviders] = useState([]);

  useEffect(() => {
    getFieldTypes();
    getAllStaff("all");
    TrackPageView("Note Settings");
    (async () => {
      const s = await getNoteSettings();
      if (s) setSettings({ ...DEFAULT_SETTINGS, ...s });
      setSettingsLoaded(true);
    })();
    (async () => {
      const providers = await getClientOtherProviders();
      setClientProviders(providers || []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await getNoteTypes(currentPage);
      setInitialLoading(false);
    })();
    getAllNoteTypes();
  }, [currentPage]);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // Optimistically merge a partial, whitelisted patch, then persist it and
  // sync local state to the server's returned document.
  const patch = async (partial) => {
    setSettings((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(partial).map(([grp, vals]) => [grp, { ...prev[grp], ...vals }])
      ),
    }));
    const updated = await updateNoteSettings(partial);
    if (updated) setSettings({ ...DEFAULT_SETTINGS, ...updated });
  };

  const staffOptions = useMemo(
    () =>
      (allStaff || [])
        .filter((s) => s?.active !== false && s?.deleted !== true && s.role ==='admin' && s.platform === 'ogiso')
        .map((s) => ({
          value: s?.id,
          label:
            s?.name || `${s?.firstName || ""} ${s?.lastName || ""}`.trim() || "Unknown",
        })),
    [allStaff]
  );

  // Candidate approvers for the Studio Settings step — admins only (a note's
  // approvers must be admins), same role/platform filter as staffOptions above.
  const admins = useMemo(
    () =>
      (allStaff || [])
        .filter(
          (s) =>
            s?.active !== false &&
            s?.deleted !== true &&
            s?.role === "admin" &&
            s?.platform === "ogiso"
        )
        .map((s) => ({
          id: s?.id,
          name: s?.name || `${s?.firstName || ""} ${s?.lastName || ""}`.trim() || "Unknown",
          role: s?.role || s?.designation || "",
        })),
    [allStaff]
  );

  // Everyone the Studio's Availability section can grant/deny a note type to.
  // Staff records only (`type === "staff"`), which deliberately leaves out:
  //   • the main admin — GET /staff/all appends the owner account to allStaff
  //     (type "admin"); it always keeps access to every note type, so it has no
  //     business being checkable here (see utils/noteTypeAccess)
  //   • contractors — Skypoint-linked accounts are unshifted onto the same list
  //     and don't author notes in this app
  const staffMembers = useMemo(
    () =>
      (allStaff || [])
        .filter(
          (s) =>
            s?.type === "staff" &&
            s?.active === true &&
            s?.deleted !== true &&
            String(s?.role || "").toLowerCase() !== "contractor"
        )
        .map((s) => ({
          id: s?.id,
          name: s?.name || `${s?.firstName || ""} ${s?.lastName || ""}`.trim() || "Unknown",
          role: s?.role || s?.designation || "",
        })),
    [allStaff]
  );

  // ---- section panels ----
  const renderGeneral = () => (
    <SettingCard classes={classes} title="General" sub="Defaults applied when staff create and submit notes.">
      <SettingRow
        classes={classes}
        label="Auto-save drafts"
        desc="Keep an editable draft as staff type, so nothing is lost if they navigate away."
        checked={settings.general.autoSaveDrafts}
        onChange={(v) => patch({ general: { autoSaveDrafts: v } })}
      />
      <SettingRow
        classes={classes}
        label="Lock notes after approval"
        desc="Approved notes become read-only; edits create a new version."
        checked={settings.general.lockAfterApproval}
        onChange={(v) => patch({ general: { lockAfterApproval: v } })}
      />
    </SettingCard>
  );

  const renderNotifications = () => (
    <SettingCard classes={classes} title="Notifications" sub="Who gets emailed as notes move through review.">
      <SettingRow
        classes={classes}
        label="Email approvers on submit"
        desc="Send an email to approvers when a note is submitted for review."
        checked={settings.notifications.emailApproversOnSubmit}
        onChange={(v) => patch({ notifications: { emailApproversOnSubmit: v } })}
      />
      <SettingRow
        classes={classes}
        label="Notify staff on rejection"
        desc="Email the author with the rejection reason so they can resubmit."
        checked={settings.notifications.notifyStaffOnRejection}
        onChange={(v) => patch({ notifications: { notifyStaffOnRejection: v } })}
      />
    </SettingCard>
  );

  const renderIncident = () => {
    return (
      <SettingCard
        classes={classes}
        title="Incident Notes"
        sub="Recipients notified for every incident report, regardless of severity."
      >
        <Box className={classes.srow} style={{ display: "block" }}>
          <Text className={classes.srowLab} mb={4}>
            Administrator recipients
          </Text>
          <Text className={classes.srowDsc} mb={10}>
            These administrators receive a notice for every incident report submitted.
          </Text>
          <MultiSelect
            searchable
            clearable
            radius="md"
            data={staffOptions}
            placeholder="Select administrators…"
            value={settings.incident.adminRecipients || []}
            onChange={(v) => patch({ incident: { adminRecipients: v } })}
          />
        </Box>
        <SettingRow
          classes={classes}
          label="Notify client guardians & POA"
          desc="Notify guardians and power-of-attorney contacts on the client's record."
          checked={settings.incident.notifyGuardiansPOA}
          onChange={(v) => patch({ incident: { notifyGuardiansPOA: v } })}
        />
        <Box className={classes.srow} style={{ display: "block", borderBottom: "none" }}>
          <Text className={classes.srowLab} mb={4}>
            Other providers
          </Text>
          <Text className={classes.srowDsc} mb={10}>
            Providers recorded on clients' Care &amp; Support pages, available to notify on
            incidents (read-only). Edit these on the client's profile.
          </Text>
          {clientProviders.length === 0 ? (
            <Text className={classes.srowDsc}>No client providers recorded yet.</Text>
          ) : (
            clientProviders.map((p) => (
              <Box key={p.key} className={classes.contactRow}>
                <Box className={classes.contactAv}>{initials(p.providerName) || "P"}</Box>
                <Box style={{ flex: 1 }}>
                  <Text className={classes.srowLab}>
                    {p.providerName || "Unnamed provider"}
                    {p.providerType ? ` · ${p.providerType}` : ""}
                  </Text>
                  <Text className={classes.srowDsc}>
                    {[p.providerPhone, p.providerEmail, p.clientName && `Client: ${p.clientName}`]
                      .filter(Boolean)
                      .join("  ·  ")}
                  </Text>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </SettingCard>
    );
  };

  const renderAi = () => (
    <SettingCard
      classes={classes}
      title="AI & Voice"
      sub="Assisted documentation features available to staff."
      action={
        <Text className={classes.pill}>
          {profile?.subscription?.plan?.name || "SkAi credits"}
        </Text>
      }
    >
      <SettingRow
        classes={classes}
        label="AI note generation"
        desc="Let staff generate note narratives from a short prompt (uses SkAi credits)."
        checked={settings.ai.aiNoteGeneration}
        onChange={(v) => patch({ ai: { aiNoteGeneration: v } })}
      />
      <SettingRow
        classes={classes}
        label="Voice-to-text dictation"
        desc="Enable microphone dictation in long-text fields."
        checked={settings.ai.voiceToText}
        onChange={(v) => patch({ ai: { voiceToText: v } })}
      />
    </SettingCard>
  );

  const renderReports = () => (
    <SettingCard classes={classes} title="Reports & PDF" sub="How exported note PDFs and reports are assembled.">
      <SettingRow
        classes={classes}
        label="Include agency letterhead"
        desc="Add your logo to the top of exported note PDFs."
        checked={settings.reports.includeLetterhead}
        onChange={(v) => patch({ reports: { includeLetterhead: v } })}
      />
      <SettingRow
        classes={classes}
        label="Only include approved notes in reports"
        desc="Exclude pending and rejected notes from generated reports."
        checked={settings.reports.approvedNotesOnly}
        onChange={(v) => patch({ reports: { approvedNotesOnly: v } })}
      />
    </SettingCard>
  );

  const renderTemplates = () => (
    <Box className={classes.tplPanel}>
      <Box className={classes.tplPanelHead}>
        <Text className={classes.cardHeadTitle}>Templates</Text>
        <Box className={classes.spacer} />
        <Button
          radius="md"
          className={classes.btnGhost}
          leftIcon={<LuListChecks size={16} />}
          onClick={() => navigate("/home/activitylibrary")}
        >
          Quick Notes
        </Button>
        <Button
          radius="md"
          className={classes.btnPrimary}
          leftIcon={<LuPlus size={16} />}
          onClick={() => openStudio(null)}
        >
          Add Template
        </Button>
      </Box>

      <Box className={classes.tplPanelBody}>
        {loading || initialLoading ? (
          <Box className={classes.loader}>
            <Loader size="lg" color="var(--app-primary)" />
          </Box>
        ) : (
          <>
            <Grid m={0}>
              {noteTypes?.ntypes?.map((note, i) => (
                <Grid.Col key={note?.id || i} xs={12} sm={6} lg={4}>
                  <NoteCard note={note} onOpenBuilder={openStudio} />
                </Grid.Col>
              ))}
              <Grid.Col xs={12} sm={6} lg={4}>
                <Box className={classes.tplNew} onClick={() => openStudio(null)}>
                  <LuPlus size={26} />
                  <Text fw={700}>Add Template</Text>
                  <Text fz={12}>Build fields & sections for a new note type</Text>
                </Box>
              </Grid.Col>
            </Grid>
            <CustomPagination
              itemsPerPage={20}
              data={noteTypes?.count}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
            />
          </>
        )}
      </Box>
    </Box>
  );

  const panels = {
    templates: renderTemplates,
    general: renderGeneral,
    notifications: renderNotifications,
    incident: renderIncident,
    ai: renderAi,
    reports: renderReports,
  };

  const groups = ["Configuration", "Account"];

  return (
    <Box className={classes.page}>
      <AppNavbar title="Note Settings" profile={profile} />

      {studio.open ? (
        <NoteTypeStudio
          note={studio.note}
          admins={admins}
          staffList={staffMembers}
          onClose={closeStudio}
          onSaved={refreshTypes}
          readOnly={!!studio.note?.general}
        />
      ) : (
        <>
          <Box
            component="button"
            className={classes.backlink}
            onClick={() => navigate("/home/note")}
          >
            <LuChevronLeft size={16} /> Back
          </Box>
          <Text className={classes.sectitle}>Note settings</Text>

          <Box className={classes.setwrap}>
            {/* master nav */}
            <Box className={classes.setnav}>
              {groups.map((g) => (
                <Box key={g}>
                  <Text className={classes.navGroup}>{g}</Text>
                  {NAV.filter((n) => n.group === g).map((n) => (
                    <button
                      key={n.key}
                      className={cx(classes.navBtn, section === n.key && classes.navBtnActive)}
                      onClick={() => setSection(n.key)}
                    >
                      {n.icon}
                      {n.label}
                    </button>
                  ))}
                </Box>
              ))}
            </Box>

            {/* detail */}
            <Box>
              {section !== "templates" && !settingsLoaded ? (
                <Box className={classes.loader}>
                  <Loader size="lg" color="var(--app-primary)" />
                </Box>
              ) : (
                panels[section]()
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default NewNoteType;
